import type { Ref } from 'vue';
import { useCheckoutSdk } from './useCheckoutSdk';
import { resolveEffectivePlanId } from '../shared/utils/roundPayments';
import type { UpsalePurchase } from '../interfaces/upsalePurchase.interface';
import type { RedirectReturnResult } from '../types/redirectReturn';
import type { PaymentModel } from '~/common/models/plan.models';

interface CheckoutFormProps {
  configs: PaymentModel;
  upsale: boolean;
  stage?: string;
  isSuperDiscount?: boolean;
}

type CheckoutFormEmit = {
  (event: 'on-success', payload?: number): void;
  (event: 'action'): void;
  (event: 'redirect-return', result: RedirectReturnResult): void;
};

export function useCheckoutForm(
  props: CheckoutFormProps,
  emit: CheckoutFormEmit,
  googlePayRef: Ref<any>,
) {
  const store = useKegelAnalyticStore();
  const dialogStore = useDialogStore();
  const gatewayStore = useKegelGatewayStore();

  const isShowGPfakeButton = ref(false);
  const isShowCreditCardForm = ref(false);
  const effectivePlanId = computed(() =>
    resolveEffectivePlanId(gatewayStore.provider, props.configs.chargebeeId));

  const {
    buyCardUpsale,
    currentPaymentMethod,
    isShowApplePay,
    isApplePayAvailable,
    setApplePayAvailable,
    isShowPayPal,
    isPayPalBlockedInApp,
    setPayPalAvailable,
    isShowGpay,
    paymentMethods,
  } = payments();

  const { isGooglePayFailed, loadGooglePay } = useGooglePayButton();
  const isShowGooglePay = computed(() => isShowGpay.value && !isGooglePayFailed.value);

  const {
    status: sdkStatus,
    isOffline: isSdkOffline,
    load: loadPaymentSdk,
    lagoPaymentIntent,
    hasLagoIntentFailed,
    ensureLagoIntent,
    refreshLagoIntent,
    dropLagoIntent,
  } = useCheckoutSdk({
    planCode: () => props.configs.chargebeeId,
    roundedPrice: () => props.configs.roundedDiscPrice,
    discPrice: () => props.configs?.discPrice,
    upsale: () => props.upsale,
  });

  const isSdkReady = computed(() => sdkStatus.value === 'ready');

  const { consumeRedirectReturn } = useHyperswitchRedirectReturn();

  async function refreshGooglePayButton(): Promise<void> {
    await nextTick();
    await loadGooglePay({
      amount: props.configs.roundedDiscPrice,
      container: googlePayRef.value,
    });
  }

  function onSuccess(payload: number): void {
    emit('on-success', payload);
  }

  function handleAction(): void {
    emit('action');
  }

  function handleUpsale(paymentData: UpsalePurchase): void {
    buyCardUpsale({
      ...paymentData,
      item: effectivePlanId.value,
      web_user_uuid: store.userUuid,
      stage: props.stage,
      amount: props.configs?.roundedDiscPrice,
    });
  }

  onMounted(async () => {
    isShowCreditCardForm.value = true;

    const returned = await consumeRedirectReturn({
      item: resolveEffectivePlanId(gatewayStore.provider, props.configs?.chargebeeId),
      planCode: props.configs?.chargebeeId,
      amount: props.configs?.roundedDiscPrice,
      discPrice: props.configs?.discPrice,
      upsale: props.upsale,
    });

    emit('redirect-return', returned);

    if (returned.outcome === 'finalised' && returned.belongsToThisForm) {
      emit('on-success');

      return;
    }

    if (!props.upsale) void loadPaymentSdk();
  });

  watch(isSdkReady, (ready) => {
    if (ready) void refreshGooglePayButton();
  });

  watch(() => [props.configs.roundedDiscPrice, effectivePlanId.value], () => void refreshGooglePayButton());

  watch(() => currentPaymentMethod.value, (next: number, previous: number) => {
    if (next === 1 && previous === 0) void refreshGooglePayButton();
  });

  watch(() => props.isSuperDiscount, (value: boolean | undefined) => {
    if (value) void refreshGooglePayButton();
  });

  onBeforeUnmount(() => {
    dialogStore.setLoader(false);
  });

  return {
    store,
    dialogStore,
    gatewayStore,
    isShowGPfakeButton,
    isShowCreditCardForm,
    effectivePlanId,
    currentPaymentMethod,
    isShowApplePay,
    isApplePayAvailable,
    setApplePayAvailable,
    isShowPayPal,
    isPayPalBlockedInApp,
    setPayPalAvailable,
    isShowGpay,
    isShowGooglePay,
    paymentMethods,
    isSdkOffline,
    isSdkReady,
    lagoPaymentIntent,
    hasLagoIntentFailed,
    ensureLagoIntent,
    refreshLagoIntent,
    dropLagoIntent,
    onSuccess,
    handleAction,
    handleUpsale,
    refreshGooglePayButton,
  };
}
